import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import {
  giftablePlans,
  PLAN_GRANT_NOTE_MAX,
  salonPlanGrantRequestSchema,
  type GiftablePlan,
  type SalonPlanGrantResult,
  type SalonRow,
} from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { formatDate, plural } from '@/shared/lib/format.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { Field } from '@/shared/ui/field.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { planLabel } from '../labels.ts'
import {
  PLAN_GRANT_PRESETS,
  addMonths,
  defaultGiftPlan,
  grantDateBounds,
  hasPaidPeriod,
  toDateInput,
} from '../plan-grant.ts'
import { useGrantPlan } from '../use-salons.ts'

type Salon = NonNullable<SalonRow['salon']>

interface PlanGrantDialogProps {
  salon: Salon
  onClose: () => void
  onDone: (result: SalonPlanGrantResult) => void
}

const DEFAULT_MONTHS = 1

// A bare YYYY-MM-DD parses as UTC midnight; noon keeps the same day in any timezone.
const formatDay = (day: string): string => formatDate(`${day}T12:00:00`)

const monthsLabel = (months: number): string => `${months} ${plural(months, ['месяц', 'месяца', 'месяцев'])}`

/** «Подарить тариф»: a paid plan free until a date, then the salon falls to the free plan unless it pays. */
export function PlanGrantDialog({ salon, onClose, onDone }: PlanGrantDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const pressedOnBackdrop = useRef(false)
  const now = useMemo(() => new Date(), [])
  const bounds = grantDateBounds(now)
  const [plan, setPlan] = useState<GiftablePlan>(() => defaultGiftPlan(salon))
  const [until, setUntil] = useState(() => toDateInput(addMonths(now, DEFAULT_MONTHS)))
  const [note, setNote] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const grant = useGrantPlan()
  const paid = hasPaidPeriod(salon, now)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const close = () => {
    if (!grant.isPending) dialogRef.current?.close()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (grant.isPending || paid) return

    const parsed = salonPlanGrantRequestSchema.safeParse({ plan, until, note: note || undefined })
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Проверьте тариф и дату.')
      return
    }
    if (until < bounds.min || until > bounds.max) {
      setValidationError(`Дата — с ${formatDay(bounds.min)} по ${formatDay(bounds.max)}.`)
      return
    }
    setValidationError(null)
    grant.mutate(
      { id: salon.id, request: parsed.data },
      {
        onSuccess: (result) => {
          onDone(result)
          dialogRef.current?.close()
        },
      },
    )
  }

  const error =
    validationError ??
    (grant.error ? (grant.error instanceof ApiError ? grant.error.message : 'Не получилось — попробуйте ещё раз.') : null)

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onCancel={(event) => grant.isPending && event.preventDefault()}
      onMouseDown={(event) => {
        pressedOnBackdrop.current = event.target === event.currentTarget
      }}
      onClick={(event) => {
        if (pressedOnBackdrop.current && event.target === event.currentTarget && !note.trim()) close()
      }}
      aria-labelledby="plan-grant-title"
      className="m-auto max-h-[calc(100dvh-24px)] w-[min(480px,calc(100vw-24px))] overflow-y-auto overscroll-contain rounded-panel border border-line bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]"
    >
      <form onSubmit={submit} className="flex flex-col gap-4 px-5 py-5 md:px-6">
        <header className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2
              id="plan-grant-title"
              className="font-display text-[19px] leading-tight font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]"
            >
              Подарить тариф
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-muted [overflow-wrap:anywhere]">
              {salon.title}
              {salon.plan && ` · сейчас ${planLabel(salon.plan)}`}
              {salon.planRenewsAt && ` до ${formatDate(salon.planRenewsAt)}`}
              {salon.planGift && ' (подарок)'}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={grant.isPending}
            aria-label="Закрыть"
            className="-mt-1.5 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50 md:size-9"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>

        {paid ? (
          <Alert>
            У салона оплаченный период до {formatDate(salon.planRenewsAt!)} — подарок его не заменит. Подарить можно, когда
            он закончится.
          </Alert>
        ) : (
          <>
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1.5 text-[13px] font-medium text-ink-soft">Тариф</legend>
              <div className="flex flex-wrap gap-1.5">
                {giftablePlans.map((option) => (
                  <Chip key={option} label={planLabel(option)} active={plan === option} onClick={() => setPlan(option)} />
                ))}
              </div>
            </fieldset>

            <div className="flex flex-col gap-2">
              <Field
                label="Бесплатно до"
                type="date"
                value={until}
                min={bounds.min}
                max={bounds.max}
                required
                onChange={(event) => {
                  setUntil(event.target.value)
                  setValidationError(null)
                }}
              />
              <div role="group" aria-label="Быстрый выбор срока" className="flex flex-wrap gap-1.5">
                {PLAN_GRANT_PRESETS.map((months) => {
                  const value = toDateInput(addMonths(now, months))
                  return (
                    <Chip
                      key={months}
                      tone="tint"
                      label={monthsLabel(months)}
                      active={until === value}
                      onClick={() => {
                        setUntil(value)
                        setValidationError(null)
                      }}
                    />
                  )
                })}
              </div>
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="flex items-baseline justify-between gap-2.5">
                <span className="text-[13px] font-medium text-ink-soft">Заметка для журнала</span>
                <span className="tabular text-[12px] text-ink-faint">
                  {note.length}/{PLAN_GRANT_NOTE_MAX}
                </span>
              </span>
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={2}
                maxLength={PLAN_GRANT_NOTE_MAX}
                placeholder="Например: 3 месяца по договорённости, потом до 6 мастеров."
                className="resize-y rounded-control border border-line-strong bg-surface-soft px-3.5 py-2.5 text-[16px] text-ink outline-none placeholder:text-ink-muted focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-ring)] md:text-[14.5px]"
              />
            </label>

            <p className="text-[12.5px] text-ink-muted text-balance-pretty">
              Салон получит {planLabel(plan)} без оплаты. Когда дата пройдёт, он перейдёт на бесплатный тариф без
              онлайн-записи, если не оплатит.
            </p>
          </>
        )}

        {error && <Alert>{error}</Alert>}

        <div className="flex flex-wrap justify-end gap-2 border-t border-line-soft pt-4 max-sm:[&>button]:min-h-11 max-sm:[&>button]:flex-1">
          <Button variant="secondary" size="sm" disabled={grant.isPending} onClick={close}>
            {paid ? 'Закрыть' : 'Отмена'}
          </Button>
          {!paid && (
            <Button type="submit" size="sm" loading={grant.isPending}>
              Подарить {planLabel(plan)}
            </Button>
          )}
        </div>
      </form>
    </dialog>
  )
}
